import { Box, Grid } from '@mui/material'
import React, { useLayoutEffect, useRef } from 'react'
import i18n from 'simple-react-i18n'
import PropTypes from 'prop-types'
import { isNil } from 'lodash'
import { formatThousands, removeThousandsSeparators } from '../../../utils/RPQSUtils'
import { CHECKBOX, FIELD_TYPE_FLOAT, FIELD_TYPE_INT } from 'rpqs/constants/RpqsConstant'
import Checkbox from 'components/forms/Checkbox'
import { NewTable } from 'components/datatable/NewTable'

// Saisie intermédiaire autorisée : '-', '12,' ou '' le temps de la frappe.
const INTEGER_REGEX = /^-?\d*$/
const DECIMAL_REGEX = /^-?\d*[.,]?\d*$/

const isAllowedValue = (value, fieldType) => {
    if (fieldType === FIELD_TYPE_INT) return INTEGER_REGEX.test(value)
    if (fieldType === FIELD_TYPE_FLOAT) return DECIMAL_REGEX.test(value)
    return true
}

// Position dans la chaîne formatée qui précède `nbChars` caractères hors séparateurs.
const getCaretPosition = (formatted, nbChars) => [...formatted].reduce((acc, char, index) => {
    if (acc.count >= nbChars) return acc
    return { count: acc.count + (/\s/.test(char) ? 0 : 1), position: index + 1 }
}, { count: 0, position: 0 }).position

export const InputTable = ({
    value='',
    onChange=() => {},
    readMode= false,
    fieldType,
}) => {
    const val = value ?? ''
    const isNumber = fieldType === FIELD_TYPE_INT || fieldType === FIELD_TYPE_FLOAT
    const displayedValue = isNumber ? formatThousands(val) : val

    const inputRef = useRef(null)
    const caretRef = useRef(null)

    useLayoutEffect(() => {
        if (isNil(caretRef.current) || !inputRef.current) return
        const position = getCaretPosition(inputRef.current.value, caretRef.current)
        inputRef.current.setSelectionRange(position, position)
        caretRef.current = null
    }, [val])

    const onChangeValue = e => {
        const newValue = isNumber ? removeThousandsSeparators(e.target.value) : e.target.value
        if (newValue.length < `${val}`.length || isAllowedValue(newValue, fieldType)) {
            caretRef.current = isNumber ? removeThousandsSeparators(e.target.value.slice(0, e.target.selectionStart)).length : null
            onChange(newValue)
        }
    }
    return (
        <>
            {readMode ?
                <Box
                    sx={{
                        height: '2rem',
                        width: '100px',
                        backgroundColor: 'lightgray',
                        py: 0.5,
                        px: 0.75,
                    }}
                >
                    {displayedValue}
                </Box>
                :
                <input
                    ref={inputRef}
                    style={{ maxWidth: '100px', borderRadius: '2px', margin: 0, height: '2rem' }}
                    value={displayedValue}
                    onChange={onChangeValue}
                />
            }
        </>
    )
}

InputTable.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func,
    readMode: PropTypes.bool,
    fieldType: PropTypes.string,
}

const IndicatorTable = ({
    year='',
    indicators=[],
}) => {
    const previousYear = parseInt(year) - 1

    const input = (indicator) => {
        return indicator.inputType === CHECKBOX ?
            <Checkbox
                checked={indicator[year]?.value}
                onChange={indicator[year]?.onChange}
                readMode={indicator[year]?.readMode}
            /> :
            <InputTable
                value={indicator[year]?.value}
                onChange={indicator[year]?.onChange}
                readMode={indicator[year]?.readMode}
            />
    }

    const headers = [{ key: 'indicator', value: i18n.indicator, width: '10%' }, { key: 'indicatorDenomination', value: i18n.indicatorDenomination, width: '50%' }, { key: 'previousYear', value: previousYear, width: '10%' }, { key: 'year', value: year, width: '10%' }, { key: 'unit', value: i18n.unit, width: '20%' }]

    const formatedIndicators = indicators.map((indi) => {
        const color = indi.resumption ? 'lightGray' : 'black'
        return {
            indicator: <Box sx={{ color }}>{indi.indicator}</Box>,
            indicatorDenomination:
                <Box sx={{ textWrap: 'wrap', color, pr: 1 }}>
                    {`${indi.indicatorDenomination} ${indi.resumption ? `(${i18n.automaticResumption} ${indi.resumption})` : ''}`}
                </Box>,
            previousYear:
                indi.inputType === CHECKBOX ?
                    <Checkbox
                        checked={indi[previousYear]?.value}
                        readMode
                    /> :
                    <Box sx={{ color }}>{indi[previousYear] || 0}</Box>,
            year:
                indi.resumption ?
                    <Box sx={{ py: 1, color }}>{indi[year]?.value || 0}</Box> :
                    <Box sx={{ py: 1, color }}>{input(indi)}</Box>,
            unit: <Box sx={{ color }}>{indi.unit}</Box>,
        }
    })

    return (
        <Grid sx={{ '& td': { borderBottom: 'none' } }} size={12}>
            <NewTable
                headers={headers}
                rows={formatedIndicators}
                rowsPerPageOptions={ [1000] } // Remove pagination
            />
        </Grid>
    )
}

IndicatorTable.propTypes = {
    year: PropTypes.string,
    indicators: PropTypes.arrayOf({
        unit: PropTypes.string,
        indicator: PropTypes.string,
        indicatorDenomination: PropTypes.string,
    }),
}

export default IndicatorTable